import React, { useState } from 'react';
import moment from 'moment';
import copyToClipboard from 'copy-to-clipboard';

import { DATA_TABLE_SOURCE_TENANT_BULK_IMPORT_SYNCS_FROM_GMAIL } from 'common/dataTableConstants';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import { SR_API_ACTION_UTILITY_PARTNER_ASSIGN_TO_GMAIL_SENDER } from 'api/utilityActionTypes';

import { SR_TABLE_THEME_HISTORICAL } from 'client/components/SrTable';
import SrDataTableUI from 'client/components/SrDataTableUI';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrIcon from 'client/components/SrIcon';
import SrTooltip, {
  SR_TOOLTIP_POSITION_LEFT,
  SR_TOOLTIP_POSITION_CENTER,
} from 'client/components/SrTooltip';
import UPPartnerSearchForm from 'client/components/UPPartnerSearchForm/UPPartnerSearchForm';

import { decodeGmailSenderLabel, buildSenderLabelCountMap } from 'client/helpers/gmailSenderId';

function getDataTableSettingsFromProps() {
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_TENANT_BULK_IMPORT_SYNCS_FROM_GMAIL,
    rootViewId: DATA_TABLE_SOURCE_TENANT_BULK_IMPORT_SYNCS_FROM_GMAIL,
    initialViewSettings: {
      filter: {
        utilityPartnerId: null,
      },
      loadAllData: true,
    },
    itemsPropName: 'filesToMap',
  };
  return dataTableSettings;
}

const UPAdminPendingGmailAttachmentMappingsDataTable = ({ listingId }) => {
  const [copied, setCopied] = useState(null);
  const [assignPartnerModalArgs, setAssignPartnerModalArgs] = useState(null);
  const [assignPartnerError, setAssignPartnerError] = useState(null);

  const dispatch = useDispatch();

  const formatDate = (date) => {
    return moment(date).format('MMM DD, YYYY HH:mm A');
  };

  // The raw `gmailSenderId` is a long base64 blob that decodes to `[F]<sender>-[T]sync]<noise>`.
  // We show the decoded sender name (raw value on hover) and flag senders that recur in the list.
  const renderEmailSenderId = ({ fileToMap, senderCounts }) => {
    const label = decodeGmailSenderLabel(fileToMap.gmailSenderId);
    const occurrences = senderCounts[label] || 0;

    return (
      <SrBox display="flex" alignItems="center" gap="6px">
        <SrTooltip
          className="no-icon"
          position={SR_TOOLTIP_POSITION_LEFT}
          tooltipContentStyle={{ wordBreak: 'break-all', maxWidth: '360px' }}
          label={
            <SrText font="greycliff" color="black">
              {label}
            </SrText>
          }
        >
          {fileToMap.gmailSenderId}
        </SrTooltip>
        {occurrences > 1 && (
          <SrTooltip
            bgColor="orange600"
            position={SR_TOOLTIP_POSITION_CENTER}
            icon={<SrIcon className="warning-icon" cursor="pointer" />}
          >
            This sender appears {occurrences} times
          </SrTooltip>
        )}
      </SrBox>
    );
  };

  const headers = [
    {
      content: 'Downloaded Date',
      dataTableFieldId: 'createdAt',
      width: '20%',
      style: {
        color: 'black',
        paddingTop: '8px',
        paddingBottom: '8px',
      },
    },
    {
      content: 'Email Sender',
      width: '25%',
      style: {
        color: 'black',
        paddingTop: '8px',
        paddingBottom: '8px',
      },
    },
    {
      content: 'Email Sender Id',
      width: '20%',
      style: {
        color: 'black',
        paddingTop: '8px',
        paddingBottom: '8px',
      },
    },
    {
      dataTableFieldId: 'gmailUrl',
      width: '15%',
    },
    {
      width: '20%',
    },
  ];

  const getEmailSenderFromUrl = (url) => {
    const emailSender = url.split('/')[5];
    return emailSender;
  };

  const onPartnerSelected = ({ partner, fileToMap, reloadItems }) => {
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_ASSIGN_TO_GMAIL_SENDER,
      payload: {
        utilityPartnerId: partner.id,
        gmailSenderId: fileToMap.gmailSenderId,
        note: `User-mapped from PMS messageId: ${fileToMap.gmailMessageId}`,
      },
      onSuccess: () => {
        setAssignPartnerModalArgs(null);
        return reloadItems({
          count: 1000,
          filter: {
            gmailSenderId: fileToMap.gmailSenderId,
          },
        });
      },
      onError: (error) => {
        setAssignPartnerError(error.message);
      },
    });
  };

  const assignPartnerClick = ({ fileToMap, reloadItems }) => {
    const sender = getEmailSenderFromUrl(fileToMap.url);

    return setAssignPartnerModalArgs({
      subtitle: `Assign "${sender}" file to:`,
      onClose: () => {
        setAssignPartnerModalArgs(null);
        setAssignPartnerError(null);
      },
      onPartnerSelected: (partner) =>
        onPartnerSelected({
          partner,
          fileToMap,
          reloadItems,
        }),
    });
  };

  const handleCopyClick = ({ id, url }) => {
    copyToClipboard(url);
    setCopied(id);
    setTimeout(() => {
      setCopied(null);
    }, 1500);
  };

  return (
    <div>
      <SrDataTableConnected {...getDataTableSettingsFromProps({ listingId })}>
        {({
          filesToMap,
          reloadItems,
          viewSettings,
          dataTableApiState,
          onInfiniteScrollWrapperRef,
          dataTableIsFullyLoaded,
        }) => {
          // eslint-disable-line max-len
          if (filesToMap.length === 0 && dataTableIsFullyLoaded) {
            return (
              <SrText font="greycliff" weight="500" fontSize="16px">
                All Gmail Attachment files have been mapped to a partner.
              </SrText>
            );
          }
          const senderCounts = buildSenderLabelCountMap(filesToMap);
          return (
            <div ref={onInfiniteScrollWrapperRef}>
              <SrDataTableUI
                sticky
                theme={SR_TABLE_THEME_HISTORICAL}
                dataTableViewSettings={viewSettings}
                headers={headers}
                minWidth="100%"
                rows={filesToMap.map((fileToMap) => {
                  return {
                    cells: [
                      {
                        content: formatDate(fileToMap.createdAt),
                      },
                      {
                        content: getEmailSenderFromUrl(fileToMap.url),
                      },
                      {
                        content: renderEmailSenderId({ fileToMap, senderCounts }),
                      },
                      {
                        content: (
                          <SrBox
                            onClick={() =>
                              handleCopyClick({ id: fileToMap.id, url: fileToMap.gmailUrl })
                            }
                          >
                            <SrText font="greycliff" color="blue300" cursor="pointer">
                              {copied === fileToMap.id ? 'Copied!' : 'Copy Email URL'}
                            </SrText>
                          </SrBox>
                        ),
                      },
                      {
                        content: (
                          <SrBox onClick={() => assignPartnerClick({ fileToMap, reloadItems })}>
                            <SrText font="greycliff" color="blue300" cursor="pointer">
                              Assign to Partner
                            </SrText>
                          </SrBox>
                        ),
                      },
                    ],
                  };
                })}
              />
              {dataTableApiState.inProgress && (
                <div>
                  <div className="loading-ellipses clearfix" />
                </div>
              )}
            </div>
          );
        }}
      </SrDataTableConnected>
      {assignPartnerModalArgs && (
        <UPPartnerSearchForm {...assignPartnerModalArgs} errorMessage={assignPartnerError} />
      )}
    </div>
  );
};

UPAdminPendingGmailAttachmentMappingsDataTable.displayName =
  'UPAdminPendingGmailAttachmentMappingsDataTable';

export default UPAdminPendingGmailAttachmentMappingsDataTable;
